import { memo } from "react";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";
import {
  Autocomplete,
  Chip,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent, TextFieldProps } from "@mui/material";
import type { CatalogSelectOption, GridSize } from "../types";

const FIELD_LABEL_CLASS =
  "text-xs font-medium text-[var(--secondary-foreground)] mb-1";

type CatalogFormSelectProps = {
  label: string;
  name: string;
  value: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  grid?: GridSize;
  className?: string;
  options: CatalogSelectOption[];
  allowEmpty?: boolean;
  testId?: string;
  optionTestIdPrefix?: string;
  error?: boolean;
  helperText?: string;
};

export const CatalogFormSelect = memo(function CatalogFormSelect({
  label,
  name,
  value,
  onChange,
  disabled,
  placeholder,
  required = false,
  grid = { xs: 12, md: 6 },
  className,
  options,
  allowEmpty = false,
  testId,
  optionTestIdPrefix,
  error = false,
  helperText,
}: CatalogFormSelectProps) {
  return (
    <Grid item xs={grid.xs} md={grid.md}>
      <Typography className={FIELD_LABEL_CLASS}>
        {label}
        {required ? " *" : ""}
      </Typography>
      <Select
        data-testid={testId || `ai-catalog-form-select-${name}`}
        name={name}
        value={value}
        onChange={onChange}
        fullWidth
        required={required}
        disabled={disabled}
        error={error}
        className={`${className || ""} mb-1`}
        displayEmpty
      >
        {placeholder ? (
          <MenuItem disabled={!allowEmpty} value="">
            {placeholder}
          </MenuItem>
        ) : null}
        {options.map((option, index) => (
          <MenuItem
            data-testid={
              optionTestIdPrefix ? `${optionTestIdPrefix}-${index}` : undefined
            }
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText ? (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      ) : null}
    </Grid>
  );
});

type CatalogFormTextFieldProps = {
  label: string;
  name: string;
  value: string | number;
  onChange: TextFieldProps["onChange"];
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  type?: string;
  inputProps?: Record<string, string | number>;
  grid?: GridSize;
  className?: string;
  InputProps?: TextFieldProps["InputProps"];
  testId?: string;
  error?: boolean;
  helperText?: string;
};

const CONTROL_KEYS = new Set([
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

const isDecimalNumberField = (
  inputProps?: CatalogFormTextFieldProps["inputProps"],
) => {
  const step = inputProps?.step;
  if (step === undefined) return false;
  if (step === "any") return true;

  const numericStep = Number(step);
  return Number.isFinite(numericStep) && !Number.isInteger(numericStep);
};

const isValidNumericText = (value: string, allowDecimal: boolean) => {
  if (value === "") return true;
  return allowDecimal ? /^\d*\.?\d*$/.test(value) : /^\d*$/.test(value);
};

const getNextInputValue = (
  input: HTMLInputElement,
  insertedText: string,
) => {
  const selectionStart = input.selectionStart ?? input.value.length;
  const selectionEnd = input.selectionEnd ?? input.value.length;

  return (
    input.value.slice(0, selectionStart) +
    insertedText +
    input.value.slice(selectionEnd)
  );
};

export const CatalogFormTextField = memo(function CatalogFormTextField({
  label,
  name,
  value,
  onChange,
  disabled,
  placeholder,
  required = false,
  type = "text",
  inputProps,
  grid = { xs: 12, md: 6 },
  className,
  InputProps,
  testId,
  error = false,
  helperText,
}: CatalogFormTextFieldProps) {
  const isNumberField = type === "number";
  const allowDecimal = isDecimalNumberField(inputProps);

  const handleNumberKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isNumberField) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (CONTROL_KEYS.has(event.key)) return;
    if (event.key.length !== 1) return;

    if (!isValidNumericText(event.key, allowDecimal)) {
      event.preventDefault();
      return;
    }

    const nextValue = getNextInputValue(event.currentTarget, event.key);
    if (!isValidNumericText(nextValue, allowDecimal)) {
      event.preventDefault();
    }
  };

  const handleNumberPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (!isNumberField) return;

    const pastedText = event.clipboardData.getData("text");
    const nextValue = getNextInputValue(event.currentTarget, pastedText);
    if (!isValidNumericText(nextValue, allowDecimal)) {
      event.preventDefault();
    }
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (isNumberField && !isValidNumericText(event.target.value, allowDecimal)) {
      return;
    }

    onChange?.(event);
  };

  return (
    <Grid item xs={grid.xs} md={grid.md}>
      <Typography className={FIELD_LABEL_CLASS}>
        {label}
        {required ? " *" : ""}
      </Typography>
      <TextField
        data-testid={testId || `ai-catalog-form-field-${name}`}
        name={name}
        value={value}
        onChange={handleChange}
        type={isNumberField ? "text" : type}
        inputProps={{
          inputMode: isNumberField
            ? allowDecimal
              ? "decimal"
              : "numeric"
            : undefined,
          pattern: isNumberField
            ? allowDecimal
              ? "[0-9]*[.]?[0-9]*"
              : "[0-9]*"
            : undefined,
          ...inputProps,
          "data-testid": testId || `ai-catalog-form-input-${name}`,
        }}
        onKeyDown={handleNumberKeyDown}
        onPaste={handleNumberPaste}
        fullWidth
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className={`${className || ""} mb-1`}
        InputProps={InputProps}
        error={error}
        helperText={helperText}
      />
    </Grid>
  );
});

type CatalogChipInputProps = {
  label: string;
  value: string[];
  onChange: (event: React.SyntheticEvent, newValue: string[]) => void;
  placeholder: string;
  grid?: GridSize;
  testId?: string;
  error?: boolean;
  helperText?: string;
};

export const CatalogChipInput = memo(function CatalogChipInput({
  label,
  value,
  onChange,
  placeholder,
  grid = { xs: 12, md: 12 },
  testId,
  error = false,
  helperText,
}: CatalogChipInputProps) {
  return (
    <Grid item xs={grid.xs} md={grid.md}>
      <Typography className={FIELD_LABEL_CLASS}>{label}</Typography>
      <Autocomplete
        data-testid={testId || `ai-catalog-form-chip-input-${label}`}
        multiple
        freeSolo
        options={[]}
        value={value}
        onChange={onChange}
        renderTags={(items, getTagProps) =>
          items.map((option, index) => (
            <Chip
              size="small"
              label={option}
              {...getTagProps({ index })}
              className="bg-[var(--muted)] text-[var(--secondary-foreground)] me-1"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              "data-testid": testId || `ai-catalog-form-chip-input-${label}`,
            }}
            error={error}
            helperText={helperText}
          />
        )}
        className="mb-1"
      />
    </Grid>
  );
});
